import React, { useState, useEffect } from "react";
import { FaChevronUp } from "react-icons/fa";
import { useLanguage } from "../pages/LanguageContext";

import Energy from "../images/symbole-d-eclair-vert.png";
import Eco from "../images/eco-friendlygreen-leaf-icon.png";
import Waste from "../images/icone-de-recyclage-et-de-durabilite-vert.png";

const translations = {
  en: {
    heading1: "🌱 Our Commitment to the Environment",
    heading2: "Smarter technology · Greener choices · A better tomorrow",
    paragraph:
      "At Mass Smart Solutions, sustainability isn't an add-on. It's embedded in everything we do. We design smart spaces that don’t just think for you; they also think for the planet. From cutting energy waste to minimizing installation impact, every decision we make aims to protect resources, reduce environmental harm, and promote long-term well-being for people and planet alike.",
    features: [
      {
        icon: Energy,
        alt: "Energy Saving",
        title: "Energy Efficiency",
        text: "Our automation systems are built to optimize energy consumption across lighting, HVAC, and appliances. Motion sensors, intelligent scheduling, and adaptive routines ensure energy is only used when and where it’s needed. Clients have reported up to 30% reductions in electricity use simply by shifting to smart controls, without sacrificing comfort or functionality.",
      },
      {
        icon: Eco,
        alt: "Sustainable Materials",
        title: "Eco-Friendly Materials",
        text: "We carefully select components that meet international standards for sustainability, including RoHS-compliant and low-emission materials. From recyclable switch panels to modular smart hubs, our product sourcing prioritizes long-term durability and reduced environmental impact. Even packaging is optimized for minimal waste and easy recycling.",
      },
      {
        icon: Waste,
        alt: "Waste Reduction",
        title: "Minimized Waste",
        text: "Our installation process is designed to preserve walls, surfaces, and finishes, avoiding unnecessary drilling or wiring whenever possible. With wireless solutions like Zigbee and Matter devices, we eliminate the need for heavy infrastructure, reducing debris and material use. Every site is left cleaner than we found it, and our remote support reduces unnecessary service visits, lowering emissions over time.",
      },
    ],
  },
  ar: {
    heading1: "🌱 التزامنا تجاه البيئة",
    heading2: "تقنية أذكى · خيارات أكثر إخضراراً · من أجل غدٍ أفضل",
    paragraph:
      "في ماس للحلول الذكية، لا تُعد الاستدامة ميزة إضافية، بل هي جزء أساسي من كل ما نقوم به. نصمّم مساحات ذكية لا تفكّر فقط بك، بل تفكّر أيضًا بكوكبنا. بدءاً من تقليل الهدر في استهلاك الطاقة، وصولًا إلى تقليل الأثر البيئي أثناء التركيب، كل قرار نتخذه يهدف إلى الحفاظ على الموارد وتقليل الأضرار البيئية وتعزيز رفاهية الإنسان على المدى الطويل.",
    features: [
      {
        icon: Energy,
        alt: "توفير الطاقة",
        title: "كفاءة الطاقة",
        text: "نقوم ببناء أنظمتنا الذكية لتُحسّن استهلاك الطاقة في الإضاءة وأنظمة التكييف والأجهزة الكهربائية. من خلال مستشعرات الحركة والجداول الذكية والروتينات التكيفية، نضمن استخدام الطاقة فقط عند الحاجة وفي الوقت المناسب. وقد سجّل عملاؤنا انخفاضاً في استهلاك الكهرباء بنسبة تصل إلى 30٪ بعد اعتماد أنظمة التحكم الذكية، دون أي تنازل عن الراحة أو الأداء.",
      },
      {
        icon: Eco,
        alt: "مواد صديقة للبيئة",
        title: "مواد صديقة للبيئة",
        text: "نختار مكوناتنا بعناية لتتوافق مع المعايير العالمية للاستدامة، بما في ذلك مواد منخفضة الانبعاثات ومتوافقة مع مواصفات RoHS. من الألواح القابلة لإعادة التدوير إلى الوحدات الذكية القابلة للتوسعة، نُعطي الأولوية للمتانة طويلة الأمد وتقليل التأثير البيئي. حتى التغليف نعمل على تقليله ليكون قابلاً لإعادة التدوير وسهل التخلص منه.",
      },
      {
        icon: Waste,
        alt: "تقليل النفايات",
        title: "تقليل الهدر",
        text: "تصميم عملية التركيب لدينا يراعي الحفاظ على الجدران والتشطيبات دون الحاجة إلى الحفر أو تمديد الأسلاك غير الضروري. من خلال اعتماد حلول لاسلكية مثل Zigbee وMatter، نتجنب البنية التحتية المعقدة ونُقلّل من المواد المستخدمة والمخلّفات. نغادر كل موقع نظيفًا كما وجدناه، ونوفر دعمًا عن بُعد يُقلّل من زيارات الصيانة الميدانية والانبعاثات المصاحبة لها.",
      },
    ],
  },
};

const WindTurbine = ({ size = 220 }) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 200 400"
      width={size}
      height={size * 1.6}
      style={{
        cursor: "pointer",
        transition: "filter 0.3s ease",
        filter: isHovered
          ? "drop-shadow(0 0 12px #66bb6a) drop-shadow(0 0 18px #a5d6a7)"
          : "drop-shadow(0 6px 6px rgba(0,0,0,0.15))",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label="Wind turbine illustration"
      role="img"
    >
      <defs>
        <linearGradient id="towerGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2e7d32" />
          <stop offset="100%" stopColor="#145214" />
        </linearGradient>
        <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#a5d6a7" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#388e3c" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect
        x="90"
        y="150"
        width="20"
        height="230"
        fill="url(#towerGradient)"
        rx="4"
        ry="4"
      />
      <rect
        x="60"
        y="380"
        width="80"
        height="15"
        fill="#1b5e20"
        rx="8"
        ry="8"
      />
      <circle cx="100" cy="140" r="18" fill="#66bb6a" />
      <circle cx="100" cy="140" r="30" fill="url(#hubGlow)" />
      <g
        style={{
          transformOrigin: "100px 140px",
          animation: `${
            isHovered ? "blade-spin-fast" : "blade-spin"
          } 6s linear infinite`,
        }}
      >
        <rect
          x="95"
          y="20"
          width="10"
          height="100"
          rx="5"
          ry="5"
          fill="#81c784"
        />
        <rect
          x="95"
          y="40"
          width="10"
          height="100"
          rx="5"
          ry="5"
          fill="#81c784"
          transform="rotate(120 100 140)"
        />
        <rect
          x="95"
          y="40"
          width="10"
          height="100"
          rx="5"
          ry="5"
          fill="#81c784"
          transform="rotate(240 100 140)"
        />
      </g>
      <style>{`
        @keyframes blade-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes blade-spin-fast { from { transform: rotate(0deg); } to { transform: rotate(720deg); } }

        @media (prefers-reduced-motion: reduce) {
          g[style*="animation"] { animation: none !important; }
        }
      `}</style>
    </svg>
  );
};

export default function EnvironmentalRole() {
  const { language } = useLanguage();
  const t = translations[language];
  const isAr = language === "ar";

  const [showButton, setShowButton] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);

  useEffect(() => {
    document.title = isAr
      ? "المسؤولية البيئية | ماس للحلول الذكية"
      : "Environmental Responsibility | Mass Smart Solutions";

    const handleScroll = () => setShowButton(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isAr]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <main
      style={{
        direction: isAr ? "rtl" : "ltr",
        fontFamily: isAr ? "'Cairo', sans-serif" : "'Poppins', sans-serif",
        background: "var(--env-bg)",
        padding: "2rem",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <style>{`
        :root {
          --env-bg: linear-gradient(145deg, #e8f5e9, #c8e6c9);
        }
        .dark :root, .dark body, body.dark {
          --env-bg: linear-gradient(145deg, #0f2313, #14311a);
        }
        .eco-card { transition: transform .28s ease, box-shadow .28s ease; }
        .eco-card:hover { transform: translateY(-6px); box-shadow: 0 10px 28px rgba(0,0,0,.08); }
        @media (max-width: 768px) { .hide-mobile { display:none !important; } }
        @media (prefers-reduced-motion: reduce) {
          .eco-card:hover { transform:none; }
        }
      `}</style>

      <div
        style={{ position: "absolute", bottom: -10, left: -10, opacity: 0.12 }}
        className="hide-mobile"
      >
        <WindTurbine />
      </div>
      <div
        style={{ position: "absolute", bottom: -10, right: -10, opacity: 0.12 }}
        className="hide-mobile"
      >
        <WindTurbine />
      </div>

      <h1
        style={{
          fontSize: "clamp(1.6rem, 3.8vw, 2.6rem)",
          fontWeight: 800,
          textAlign: "center",
          marginBottom: ".5rem",
          background: "linear-gradient(to right, #2e7d32, #81c784)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          lineHeight: 1.35,
          padding: "0 1rem",
        }}
      >
        {t.heading1}
      </h1>
      <h2
        style={{
          fontSize: "clamp(1rem, 2.5vw, 1.25rem)",
          fontWeight: 600,
          textAlign: "center",
          marginTop: 0,
          marginBottom: "1.1rem",
          color: isAr ? "#1b5e20" : "#1b5e20",
          opacity: isAr ? 1 : 1,
        }}
      >
        {t.heading2}
      </h2>

      <p
        style={{
          maxWidth: "900px",
          fontSize: "1.08rem",
          lineHeight: 1.75,
          color: isAr ? "#1b5e20" : "#1b5e20",
          marginBottom: "2rem",
          textAlign: "center",
          paddingInline: "0.5rem",
        }}
      >
        {t.paragraph}
      </p>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "1.4rem",
          justifyContent: "center",
          width: "100%",
          maxWidth: 1100,
        }}
      >
        {t.features.map((feature, i) => (
          <article
            key={i}
            className="eco-card"
            style={{
              background: "white",
              border: "2px solid #a5d6a7",
              borderRadius: "16px",
              padding: "1.4rem 1.2rem",
              width: "min(100%, 330px)",
              textAlign: "center",
              boxShadow: "0 6px 20px rgba(0,0,0,0.05)",
            }}
          >
            <img
              src={feature.icon}
              alt={feature.alt}
              width={72}
              height={72}
              style={{
                width: 72,
                height: 72,
                objectFit: "contain",
                marginBottom: "0.8rem",
              }}
            />
            <h3
              style={{
                fontSize: "1.25rem",
                color: "#2e7d32",
                margin: 0,
                marginBottom: "0.35rem",
                fontWeight: 800,
              }}
            >
              {feature.title}
            </h3>
            <p
              style={{
                fontSize: "0.98rem",
                color: "#2f6f35",
                margin: 0,
                marginTop: "0.3rem",
                lineHeight: 1.6,
              }}
            >
              {expandedIndex === i
                ? feature.text
                : `${feature.text.slice(0, 120)}…`}
            </p>
            <button
              onClick={() => setExpandedIndex(expandedIndex === i ? null : i)}
              style={{
                background: "none",
                color: "#1b5e20",
                border: "none",
                cursor: "pointer",
                fontWeight: 800,
                marginTop: "0.6rem",
                padding: "0.2rem 0.4rem",
              }}
              aria-expanded={expandedIndex === i}
              aria-controls={`feature-${i}`}
            >
              {expandedIndex === i
                ? isAr
                  ? "عرض أقل"
                  : "Show Less"
                : isAr
                ? "اقرأ المزيد"
                : "Read More"}
            </button>
          </article>
        ))}
      </div>

      <div
        style={{
          background: "rgba(241, 248, 233, 0.85)",
          marginTop: "3rem",
          padding: "2rem",
          borderRadius: "16px",
          textAlign: "center",
          maxWidth: "850px",
          width: "100%",
          boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
          backdropFilter: "saturate(130%) blur(2px)",
        }}
      >
        <p
          style={{
            fontSize: "1.15rem",
            color: "#2e7d32",
            marginBottom: "1rem",
            whiteSpace: "pre-wrap",
            lineHeight: 1.6,
            marginTop: 0,
          }}
        >
          {isAr
            ? "هل ترغب بالتحوّل إلى حلول أكثر استدامة دون التضحية بالتكنولوجيا؟\nدع ماس للحلول الذكية تساعدك..."
            : "Ready to go green without compromising on innovation?\nLet Mass Smart Solutions help you..."}
        </p>
        <a
          href="/contact"
          style={{
            display: "inline-block",
            marginTop: "0.3rem",
            padding: "12px 22px",
            backgroundColor: "#00A86B",
            color: "white",
            borderRadius: "12px",
            textDecoration: "none",
            fontWeight: 800,
            fontSize: "1.02rem",
            transition: "filter .2s ease",
            boxShadow: "0 6px 16px rgba(0,0,0,.08)",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.filter = "brightness(1.05)")
          }
          onMouseLeave={(e) => (e.currentTarget.style.filter = "none")}
        >
          {isAr
            ? "👈 تواصل معنا اليوم وابدأ مستقبلًا أكثر ذكاءً وبيئية"
            : "👉 Contact us today and start building your eco-smart future"}
        </a>
      </div>

      {showButton && (
        <button
          onClick={scrollToTop}
          aria-label={isAr ? "العودة إلى الأعلى" : "Back to top"}
          style={{
            position: "fixed",
            bottom: "30px",
            left: "30px",
            width: "48px",
            height: "48px",
            borderRadius: "50%",
            backgroundColor: "#1b5e20",
            color: "#fff",
            border: "none",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontSize: "20px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
            cursor: "pointer",
          }}
        >
          <FaChevronUp />
        </button>
      )}
    </main>
  );
}
